import React, { useState } from "react";
import {
  BOOK_A_DEMO_REQUESTS_TYPE,
  INDUSTRY_LIST,
  NUMBER_OF_EMPLOYEES,
  COUNTRY_MOBILE_CODES,
} from "../../data/data";
import { Link, useNavigate } from "react-router-dom";

const BookADemo = () => {
  const [formData, setFormData] = useState({
    request_type: "",
    first_name: "",
    last_name: "",
    job_title: "",
    email: "",
    country_code: "",
    mobile_number: "",
    company_name: "",
    hq_country: "",
    industry: "",
    no_of_employees: "",
    tell_us_more: "",
    how_did_you_hear_about_us: "",
  });

  const navigate = useNavigate();

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic client-side validation
    if (
      !formData.first_name.trim() ||
      !formData.last_name.trim() ||
      !formData.email.trim() ||
      !formData.country_code ||
      !formData.mobile_number.trim() ||
      !formData.company_name.trim() ||
      !formData.hq_country ||
      !formData.industry ||
      !formData.no_of_employees ||
      !formData.tell_us_more.trim()
    ) {
      setStatus("error");
      setMessage("Please fill all required fields.");
      return;
    }

    // Optional: very basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    setStatus("loading");
    setMessage("");

    const payload = {
      request_type: formData.request_type,
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      job_title: formData.job_title.trim(),
      email: formData.email.trim(),
      mobile_number: `${formData.country_code}${formData.mobile_number.trim()}`,
      company_name: formData.company_name.trim(),
      hq_country: formData.hq_country,
      industry: formData.industry,
      no_of_employees: formData.no_of_employees,
      tell_us_more: formData.tell_us_more.trim(),
      how_did_you_hear_about_us: formData.how_did_you_hear_about_us.trim(),
    };

    // console.log("Submitting payload:", payload); // ← helpful for debugging

    try {
      const response = await fetch(
        "https://sattva-backend.demetrix.in/contact/book_demo",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error("Server error:", response.status, responseData);
        throw new Error(
          responseData.message || `Server error (${response.status})`
        );
      }

      setStatus("success");
      setMessage(
        "Thank you! Your request has been submitted successfully."
      );

      // Reset form
      // setFormData({
      //   request_type: "",
      //   first_name: "",
      //   last_name: "",
      //   job_title: "",
      //   email: "",
      //   country_code: "",
      //   mobile_number: "",
      //   company_name: "",
      //   hq_country: "",
      //   industry: "",
      //   no_of_employees: "",
      //   tell_us_more: "",
      //   how_did_you_hear_about_us: "",
      // });
    } catch (error) {
      console.error("Submission failed:", error);
      setStatus("error");
      setMessage(
        error.message.includes("Failed to fetch")
          ? "Network error – please check your internet or try again later."
          : "Failed to submit. Please try again or contact support."
      );
    } finally {
      setTimeout(() => {
        if (status !== "success") setStatus("idle");
      }, 6000); // auto-clear error/success after 6s (optional)
    }
  };

  return (
    <article className="min-h-[calc(100vh-200px)] flex flex-col items-center px-4 py-16 bg-gray-50 dark:bg-[#0F172A]">
      {/* Heading */}
      <div className="text-center max-w-3xl mb-10">
        <h6 className="text-blue-600 text-base font-medium uppercase mb-2" onClick={()=>navigate('/contact')}>
          Book a Demo
        </h6>
        <h3 className="mb-4 md:text-2xl text-xl font-semibold dark:text-white">
          See Sattva in Action
        </h3>
        <p className="text-slate-500 dark:text-slate-300 max-w-xl mx-auto">
          Discover a powerful ESG management and sustainability platform
          tailored for modern organizations.
        </p>
      </div>

      {/* Form */}
      <section className="w-full flex justify-center">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-4xl bg-white dark:bg-[#020617] rounded-2xl shadow-lg p-6 md:p-10 flex flex-col gap-5"
          noValidate // ← prevents browser default validation bubbles
        >
          <select
            name="request_type"
            value={formData.request_type}
            onChange={handleChange}
            className="form-input"
            required
          >
            <option value="" disabled>
              Type of contact request *
            </option>
            {BOOK_A_DEMO_REQUESTS_TYPE.map((item) => (
              <option key={item.id} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <div className="grid md:grid-cols-2 gap-4">
            <input
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="form-input"
              type="text"
              placeholder="First Name *"
              required
            />
            <input
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="form-input"
              type="text"
              placeholder="Last Name *"
              required
            />
          </div>

          <input
            name="job_title"
            value={formData.job_title}
            onChange={handleChange}
            className="form-input"
            type="text"
            placeholder="Job Title"
          />

          <input
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="form-input"
            type="email"
            placeholder="Email *"
            required
          />

          <div className="grid grid-cols-3 gap-4">
            <select
              name="country_code"
              value={formData.country_code}
              onChange={handleChange}
              className="form-input col-span-1"
              required
            >
              <option value="" disabled>
                Code *
              </option>
              {COUNTRY_MOBILE_CODES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.code}
                </option>
              ))}
            </select>

            <input
              name="mobile_number"
              value={formData.mobile_number}
              onChange={handleChange}
              className="form-input col-span-2"
              type="tel"
              placeholder="Phone Number *"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <input
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className="form-input"
              type="text"
              placeholder="Company Name *"
              required
            />

            <select
              name="hq_country"
              value={formData.hq_country}
              onChange={handleChange}
              className="form-input"
              required
            >
              <option value="" disabled>
                HQ Country *
              </option>
              {COUNTRY_MOBILE_CODES.map((item) => (
                <option key={item.name} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className="form-input"
              required
            >
              <option value="" disabled>
                Industry *
              </option>
              {INDUSTRY_LIST.map((item) => (
                <option key={item.id} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            <select
              name="no_of_employees"
              value={formData.no_of_employees}
              onChange={handleChange}
              className="form-input"
              required
            >
              <option value="" disabled>
                Number of Employees *
              </option>
              {NUMBER_OF_EMPLOYEES.map((item) => (
                <option key={item.id} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <textarea
            name="tell_us_more"
            value={formData.tell_us_more}
            onChange={handleChange}
            rows={4}
            placeholder="Tell us more about your needs *"
            className="form-input h-20 textarea"
            required
          />

          <input
            name="how_did_you_hear_about_us"
            value={formData.how_did_you_hear_about_us}
            onChange={handleChange}
            className="form-input"
            type="text"
            placeholder="How did you hear about us?"
          />

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Sattva will use the contact information you share to communicate
            with you about our services and insights. You may unsubscribe at any
            time. Read our{" "}
            <Link to="/about" className="underline hover:text-blue-600">
              Privacy Policy
            </Link>
            .
          </p>

          {status === "loading" && (
            <p className="text-blue-600 text-center font-medium">
              Submitting...
            </p>
          )}
          {status === "success" && (
            <p className="text-green-600 text-center font-medium">
              {message}
            </p>
          )}
          {status === "error" && (
            <p className="text-red-600 text-center font-medium">{message}</p>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className={`w-full md:w-fit px-8 py-3 mt-2 rounded-full text-white font-medium transition self-center ${
              status === "loading"
                ? "bg-green-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 active:bg-green-800"
            }`}
          >
            {status === "loading" ? "Submitting..." : "Submit Request"}
          </button>
        </form>
      </section>
    </article>
  );
};

export default BookADemo;